import { Button, Card, Modal, message } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { CourseTime } from '../../components/CourseTime';
import { EmptyState } from '../../components/EmptyState';
import { StatusBadge } from '../../components/StatusBadge';
import { newClientToken } from '../../lib/client-token';
import { ApiError } from '../../lib/http';
import { CANCEL_RULE, canSelfCancel, courseStatusLabel, enrollmentLabel, remainingSeats } from '../../lib/status';
import { coursesApi } from '../../services/resources';
import { getUser } from '../../stores/session';
import type { Course, Enrollment } from '../../types/api';

export function StudentCoursesPage() {
  const [items, setItems] = useState<Course[]>([]);
  const [mine, setMine] = useState<Record<string, Enrollment>>({});
  const [error, setError] = useState('');
  const me = getUser();

  const load = useCallback(async () => {
    try {
      const page = await coursesApi.list({ page: 1, limit: 50 });
      setItems(page.items);
      const map: Record<string, Enrollment> = {};
      for (const c of page.items) {
        const ens = await coursesApi.enrollments(c.id);
        const hit = ens.items.find((e) => e.user_id === me?.id);
        if (hit) map[c.id] = hit;
      }
      setMine(map);
      setError('');
    } catch {
      setError('活动列表失败');
    }
  }, [me?.id]);

  useEffect(() => {
    void load();
  }, [load]);

  if (error) return <EmptyState text={error} />;
  if (items.length === 0) return <EmptyState text="本周暂无开放实验课。" />;

  return (
    <div className="stack">
      <h1 className="page-title">活动</h1>
      {items.map((c) => (
        <CourseCard key={c.id} course={c} mine={mine[c.id]} onChanged={() => void load()} />
      ))}
    </div>
  );
}

function CourseCard(props: { course: Course; mine?: Enrollment; onChanged: () => void }) {
  const c = props.course;
  const left = remainingSeats(c);
  const mine = props.mine;

  function confirmEnroll() {
    Modal.confirm({
      title: `确认报名「${c.title}」？`,
      content: CANCEL_RULE,
      okText: '确认报名',
      onOk: async () => {
        try {
          const data = await coursesApi.enroll(c.id, newClientToken());
          message.success(data.status === 'waitlisted' ? '已候补' : '已报名');
          props.onChanged();
        } catch (e) {
          message.error(e instanceof ApiError ? e.message : '报名失败');
        }
      },
    });
  }

  return (
    <Card title={c.title}>
      <CourseTime course={c} />
      <p>余位 {left === 0 ? '已满' : left}</p>
      <StatusBadge status={c.status} label={courseStatusLabel(c.status)} />
      {mine ? <StatusBadge status={mine.status} label={enrollmentLabel(mine.status)} /> : null}
      {!mine || mine.status === 'cancelled' ? (
        <Button
          type="primary"
          className="touch"
          disabled={c.status !== 'published'}
          onClick={confirmEnroll}
        >
          {mine?.status === 'cancelled' ? '重新报名' : '报名'}
        </Button>
      ) : (
        <Button
          danger
          className="touch"
          disabled={!canSelfCancel(c.starts_at)}
          onClick={async () => {
            await coursesApi.cancelEnrollment(c.id, mine.id);
            props.onChanged();
          }}
        >
          取消报名
        </Button>
      )}
    </Card>
  );
}
